import React, { FC, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import { colors } from "./styles";
import type {
  ContestedLossEntry,
  RaceDay,
  RaceEntryTimeEntry,
} from "./types";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

ChartJS.defaults.color = colors.textMuted;
ChartJS.defaults.font.family =
  '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif';

const GREEN = colors.ok;
const GREY = colors.textDim;
const PURPLE = colors.purple;

function fmtOffset(ms: number | null): string {
  if (ms == null) return "no tap recorded";
  const s = ms / 1000;
  if (s < 60) return `+${s.toFixed(2)}s`;
  const m = s / 60;
  return `delayed +${m < 60 ? `${m.toFixed(0)}m` : `${(m / 60).toFixed(1)}h`}`;
}

function winRateTone(rate: number): string {
  if (rate >= 0.5) return GREEN;
  if (rate >= 0.25) return colors.warn;
  return colors.err;
}

const gridColor = "rgba(255,255,255,0.06)";

const ChartBox: FC<{ height: number; children: React.ReactNode }> = ({
  height,
  children,
}) => <div style={{ height, position: "relative" }}>{children}</div>;

/** Daily timeline: one bar per race day, height = #racers, colour = contested. */
export const DailyRaceTimeline: FC<{ perDay: RaceDay[] }> = ({ perDay }) => {
  const { data, options } = useMemo(() => {
    const labels = perDay.map((d) => d.date.slice(5, 10)); // MM-DD
    const data = {
      labels,
      datasets: [
        {
          label: "racers",
          data: perDay.map((d) => d.participantCount),
          backgroundColor: perDay.map((d) => (d.contested ? GREEN : GREY)),
          borderWidth: 0,
          borderRadius: 2,
          categoryPercentage: 0.9,
          barPercentage: 0.95,
        },
      ],
    };
    const options: ChartOptions<"bar"> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: TooltipItem<"bar">[]) =>
              perDay[items[0].dataIndex]?.date ?? "",
            label: (item: TooltipItem<"bar">) => {
              const d = perDay[item.dataIndex];
              if (!d) return "";
              const who = `${d.winnerEmoji ? `${d.winnerEmoji} ` : ""}${d.winner}`;
              return [
                `${who} won`,
                `${d.contested ? "⚔️ contested" : "⚪ solo"} · ${d.participantCount} racer${d.participantCount === 1 ? "" : "s"}`,
                `tap ${fmtOffset(d.winnerOffsetMs)}`,
              ];
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 90, minRotation: 90, autoSkip: true, font: { size: 9 } } },
        y: {
          beginAtZero: true,
          ticks: { stepSize: 1, precision: 0 },
          grid: { color: gridColor },
          title: { display: true, text: "racers", color: colors.textDim },
        },
      },
    };
    return { data, options };
  }, [perDay]);

  return (
    <ChartBox height={200}>
      <Bar data={data} options={options} />
    </ChartBox>
  );
};

/** Horizontal win-rate bars (contested only), biggest loser at top. */
export const WinRateBars: FC<{ contested: ContestedLossEntry[] }> = ({
  contested,
}) => {
  const { data, options } = useMemo(() => {
    const rows = contested;
    const data = {
      labels: rows.map((c) => `${c.userEmoji ? `${c.userEmoji} ` : ""}${c.username}`),
      datasets: [
        {
          label: "win rate",
          data: rows.map((c) => Math.round(c.winRate * 1000) / 10),
          backgroundColor: rows.map((c) => winRateTone(c.winRate)),
          borderWidth: 0,
          borderRadius: 3,
        },
      ],
    };
    const options: ChartOptions<"bar"> = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item: TooltipItem<"bar">) => {
              const c = rows[item.dataIndex];
              return `${item.formattedValue}% — ${c.wins}W / ${c.losses}L (${c.contests} contested)`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: 100,
          grid: { color: gridColor },
          ticks: { callback: (v) => `${v}%` },
        },
        y: { grid: { display: false } },
      },
    };
    return { data, options };
  }, [contested]);

  return (
    <ChartBox height={Math.max(160, contested.length * 28 + 30)}>
      <Bar data={data} options={options} />
    </ChartBox>
  );
};

/** Horizontal average-entry-time bars (first 10s window), fastest at top. */
export const AvgEntryBars: FC<{ entryTimes: RaceEntryTimeEntry[] }> = ({
  entryTimes,
}) => {
  const { data, options } = useMemo(() => {
    const rows = entryTimes;
    const data = {
      labels: rows.map((e) => `${e.userEmoji ? `${e.userEmoji} ` : ""}${e.username}`),
      datasets: [
        {
          label: "avg entry (s)",
          data: rows.map((e) => Math.round(e.avgOffsetMs) / 1000),
          backgroundColor: rows.map((_, i) => (i === 0 ? PURPLE : colors.accent)),
          borderWidth: 0,
          borderRadius: 3,
        },
      ],
    };
    const options: ChartOptions<"bar"> = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item: TooltipItem<"bar">) => {
              const e = rows[item.dataIndex];
              return `avg +${(e.avgOffsetMs / 1000).toFixed(2)}s · fastest +${(e.fastestOffsetMs / 1000).toFixed(2)}s · ${e.samples} race${e.samples === 1 ? "" : "s"}`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: { callback: (v) => `${v}s` },
          title: { display: true, text: "seconds after unlock", color: colors.textDim },
        },
        y: { grid: { display: false } },
      },
    };
    return { data, options };
  }, [entryTimes]);

  return (
    <ChartBox height={Math.max(160, entryTimes.length * 28 + 40)}>
      <Bar data={data} options={options} />
    </ChartBox>
  );
};
